import React, { useEffect, useState } from 'react';
import Drawer from '../components/Drawer';
import Footer from '../components/Footer';
import { getOrgInvitations, inviteMember } from '../api/api';
import { useAuth } from '../context/AuthContext';
import StyledSelect from '../components/StyledSelect';

const roleOptions = ['admin', 'finance_manager', 'accountant', 'project_manager', 'employee', 'viewer'];

export default function OrganizationInvitationsPage() {
  const { activeOrg, canManageMembers, canViewUsers, canInviteUsers } = useAuth();
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('employee');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadInvitations = async () => {
    if (!activeOrg?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await getOrgInvitations(activeOrg.id);
      setRows(res?.data?.data?.invitations || []);
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || 'Failed to load invitations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewUsers && !canInviteUsers) return;
    loadInvitations();
  }, [activeOrg?.id, canViewUsers, canInviteUsers]);

  if (!canViewUsers && !canInviteUsers) {
    const denied = (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl border bg-white p-6 text-center" style={{ borderColor: '#f2d8e0' }}>
          <h1 className="text-2xl font-bold text-slate-900">Invitations</h1>
          <p className="mt-2 text-sm text-slate-500">You do not have permission to view or create invitations.</p>
        </div>
      </div>
    );

    return <><Drawer pageHeader={denied} /><Footer /></>;
  }

  const onInvite = async (e) => {
    e.preventDefault();
    if (!canManageMembers || !canInviteUsers) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const res = await inviteMember(activeOrg.id, { email, org_role: role });
      const inviteCode = res?.data?.data?.invite_code;
      setSuccess(inviteCode ? `Invitation created. Invite code: ${inviteCode}` : 'Invitation created successfully.');
      setEmail('');
      setRole('employee');
      await loadInvitations();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  const pageContent = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 space-y-6">
      <section className="rounded-3xl border p-6 shadow-sm" style={{ borderColor: '#f4d6df', background: '#fff' }}>
        <h1 className="text-3xl font-bold text-slate-900">Invitations</h1>
        <p className="text-sm text-slate-500 mt-2">Invite new members by email and track code-based onboarding across the tenant.</p>
      </section>

      {error ? <Banner tone="error" text={error} /> : null}
      {success ? <Banner tone="success" text={success} /> : null}

      <section className="grid grid-cols-1 xl:grid-cols-[1.1fr_1.4fr] gap-6">
        <form onSubmit={onInvite} className="rounded-3xl border p-5 shadow-sm space-y-4" style={{ borderColor: '#f4d6df', background: '#fff' }}>
          <h2 className="text-lg font-bold text-slate-900">Create Invitation</h2>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="teammate@example.com" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
          <StyledSelect
            value={role}
            onChange={setRole}
            options={roleOptions.map((item) => ({ value: item, label: item.replace('_', ' ') }))}
            placeholder="Select role"
            searchable
          />
          <button disabled={!canManageMembers || !canInviteUsers || submitting} className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: '#c44569' }}>
            {submitting ? 'Sending Invitation...' : 'Invite Member'}
          </button>
          {!canInviteUsers ? <p className="text-xs text-slate-400">Your current role can review invitations but cannot create them.</p> : null}
        </form>

        <div className="rounded-3xl border p-5 shadow-sm overflow-auto" style={{ borderColor: '#f4d6df', background: '#fff' }}>
          {loading ? <p className="text-sm text-slate-500">Loading invitations...</p> : null}
          {!loading && (
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-3">Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Invite Code</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-50">
                    <td className="py-3">{row.email}</td>
                    <td>{row.org_role}</td>
                    <td>{row.status}</td>
                    <td>{row.invite_code || 'n/a'}</td>
                    <td>{row.expires_at || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );

  return <><Drawer pageHeader={pageContent} /><Footer /></>;
}

function Banner({ tone, text }) {
  const styles = tone === 'success'
    ? { borderColor: '#a7f3d0', background: '#ecfdf5', color: '#047857' }
    : { borderColor: '#f9b8ca', background: '#fce0e8', color: '#c44569' };
  return <div className="rounded-2xl border px-4 py-3 text-sm" style={styles}>{text}</div>;
}
